import type { StylesConfigDto } from '@builder4/shared/dto/styles';
import {
  type PageProfileOutline,
  type SectionOutlineEntry,
  LAYOUT_PATTERN_DESCRIPTIONS,
} from '../types/page-profile';
import { getContentDslReference } from './dsl-reference';
import { getCopyRulesForPrompt, getDesignRulesForPrompt } from './anti-slop-rules';
import { formatGlobalStylesForPrompt } from './format-styles';

/**
 * Build a comprehensive prompt for generating a single section
 * with combined layout + content.
 */
export const buildSectionGenerationPrompt = (params: {
  outline: PageProfileOutline;
  sectionEntry: SectionOutlineEntry;
  sectionIndex: number;
  totalSections: number;
  styles: StylesConfigDto;
}): { system: string; user: string } => {
  const { outline, sectionEntry, sectionIndex, totalSections, styles } = params;

  const styleContext = formatGlobalStylesForPrompt(styles);

  const system = `You are a landing page builder. Generate a complete section with BOTH layout containers AND content elements as XML.

## Your Task

Create a "${sectionEntry.type}" section. You are responsible for:
1. Designing the layout structure (section, row, column containers)
2. Filling in all content (text, buttons, images, forms)

Return a complete, self-contained section that is ready to render.

## Output Format

Return ONLY the complete section XML. No explanation, no markdown fencing, no commentary.
The output must be valid XML starting with <section> and ending with </section>.

${getContentDslReference()}

${styleContext}

${getCopyRulesForPrompt()}

${getDesignRulesForPrompt()}

## Hard Design Rules (MUST FOLLOW)

### Button Rules
- If 2 buttons are side-by-side, use primary + secondary variants (NEVER two primary buttons together)
- Use variant="primary" for the main CTA, variant="secondary" for secondary action
- Both buttons must be equally readable and tappable — the secondary button's text must have sufficient contrast against its background. Do not use low-contrast or washed-out colors on the secondary button just to de-emphasize it.
- No letter spacing on button text (default letterSpacing)
- Button text must be action-specific: "Start Free Trial", "Get Your Quote", "Book a Demo"
- Never use generic CTAs: "Submit", "Click Here", "Learn More", "Read More", "Find Out More"

### Image Rules
- Use fit="contain" for product images with transparency (NEVER crop product images)
- For hero images or backgrounds, fit="cover" is acceptable
- If a product image appears to be transparent, add a neutral background: bg="rgba(220,220,220,0.2)"
- Only use real assigned image URLs for bgImage attributes on containers. If no real image is assigned to this section, use a solid color (bg="#1a1a2e") or gradient (bg="gradient(linear, 135, #1a1a2e:0, #2d2d44:100)") instead. Placehold.co URLs must only appear as the src attribute on inline <image> elements.

### Typography Rules
- Use bolder font weights for impact - headlines should feel bold and commanding
- Use heading variants (h1, h2, h3) appropriately for hierarchy
- Body text containers should use maxWidth="75%" for readability (never full 100% width for body text)
- Do NOT insert literal space characters before or after text content inside <paragraph> elements to create indentation or padding. For example, write <paragraph>Your text here</paragraph>, never <paragraph> Your text here </paragraph>. Use padding/margin attributes on containers if spacing is needed.

### Text Color Rules
**Default behavior:** Omit the color attribute to inherit from global styles. The global text color is designed to work with the page background.

**When to use color overrides:**
- **Dark/accent section backgrounds:** Use color="#ffffff" or rgba(255,255,255,0.9) for contrast
- **Supporting/muted text:** Use color="rgba(0,0,0,0.6)" or similar for secondary text, captions, labels
- **Accent text:** Use color="primary" or color="secondary" for emphasis (semantic names preferred)
- **Text on colored cards:** Ensure contrast with the card's background

**When NOT to override colors:**
- Transparent sections with page background showing through - the global text color already works
- Light sections - global text color is designed for this
- Headlines and body text that should match the brand - let global styles handle it

**Use semantic color names when possible:** color="text", color="primary", color="secondary" - these pull from the generated style and maintain consistency.

**Contrast requirement (3:1 minimum):** Every text element must have at least a 3:1 contrast ratio against its immediate background. Before choosing a text color, consider what's behind it — the section bg, card bg, or page background for transparent sections. Common mistakes to avoid:
- Light gray text on a white or light background
- White text on a light accent background
- Dark text on a dark section background
- Muted/secondary text that drops below 3:1 — use dark colors from the global palette on light backgrounds, light colors from the global palette on dark backgrounds
- NEVER place white text on neon, yellow, or lime backgrounds — the contrast is unreadable
- NEVER place neon, yellow, or lime text on white or light backgrounds — equally unreadable

### Content Rules
- Do NOT use emoji characters in text content
- Use subtle neutral backgrounds (bg="rgba(220,220,220,0.2)") to create depth for:
  - Transparent PNG images
  - Card containers
  - Feature boxes
  - Testimonial cards

### Container Alignment
- Text and buttons inside the same container should use consistent alignment
- If a column has left-aligned text, buttons should also be left-aligned (not centered)

### Responsive Overrides (REQUIRED)
- EVERY section MUST have mobile-direction="column" (if using row layout)
- EVERY section MUST have mobile-padding with 60-70% of desktop values
- Columns should have mobile-width="100%" to stack on mobile

### Background Image Overlay Rules (CRITICAL)
- NEVER use position="absolute" elements to create background overlays
- Use bgImageOpacity (0-100) to control image transparency - use 25-40 for text readability
- Use bg with rgba colors to add color tint over the image
- Example: bgImage="url" bgImageOpacity="30" bg="rgba(0,0,0,0.4)"

## Advanced Styling (MUST USE)

Make this section visually BOLD and striking by using AT LEAST ONE of these advanced techniques:

1. **Gradient Backgrounds** - Bold, dramatic gradients on sections, columns, or cards
   - Syntax: gradient(type, angle, color:position, color:position, ...)
   - USE HIGH CONTRAST colors that shift dramatically across the gradient
   - Bold hero: bg="gradient(linear, 135, #1a1a2e:0, #16213e:50, #0f3460:100)"
   - Vibrant accent: bg="gradient(linear, 120, #667eea:0, #764ba2:50, #f093fb:100)"
   - Warm dramatic: bg="gradient(linear, 135, #ff6b6b:0, #feca57:100)"
   - Cool dramatic: bg="gradient(linear, 135, #4facfe:0, #00f2fe:100)"
   - Dark moody: bg="gradient(linear, 180, #0c0c0c:0, #1a1a2e:50, #2d2d44:100)"
   - AVOID subtle same-hue gradients like #ffffff to #f5f5f5 - these are invisible!

2. **Gradient Text** - Eye-catching gradient colors on headlines (USE BOLD COLORS)
   - Use color="gradient(linear, 90, #f093fb:0, #f5576c:100)" on paragraph elements
   - Purple to pink: color="gradient(linear, 90, #667eea:0, #f093fb:100)"
   - Sunset: color="gradient(linear, 90, #fa709a:0, #fee140:100)"
   - Ocean: color="gradient(linear, 90, #4facfe:0, #00f2fe:100)"
   - Works best on h1/h2 headings for maximum visual impact

3. **Card Backgrounds with Gradients** - Noticeable gradients on card containers
   - Light cards: bg="gradient(linear, 180, #ffffff:0, #e8e8e8:100)"
   - Frosted glass: bg="gradient(linear, 135, rgba(255,255,255,0.9):0, rgba(255,255,255,0.4):100)"
   - Colored cards: bg="gradient(linear, 135, #ffecd2:0, #fcb69f:100)"
   - Dark cards: bg="gradient(linear, 180, #2d2d44:0, #1a1a2e:100)"

4. **Absolutely Positioned Elements** - Decorative elements ONLY (NOT for overlays)
   - Floating images, decorative shapes, or accent elements
   - Use with parent container having position="relative"
   - Great for overlapping designs or visual accents
   - NEVER use for background image overlays - use bgImageOpacity instead

5. **Text Stroke/Outline** - Outlined text for bold typography
   - Use stroke attribute on paragraph elements
   - Works well for large display text

6. **Asymmetric Spacing** - Intentionally uneven padding/margins
   - Creates dynamic, modern feel
   - Example: padding="120,40,60,40" instead of uniform values

7. **Background Images with Overlay** - Darken/lighten images for text readability
   - Use bgImageOpacity="30-40" to aggressively fade the image
   - Combine with bg using a dark rgba color for color tint overlay
   - NEVER use position="absolute" elements to create overlays

8. **Shape Dividers** - Decorative SVG edge on sections with a background image
   - Use when this section has a bgImage to create a smooth visual transition at its edge
   - Syntax: shapeDivider="wave" shapeDividerPosition="bottom" shapeDividerHeight="80"
   - Types: wave, zigzag, curve, bowl, tilt
   - Color auto-resolves to match the neighboring section — do NOT set shapeDividerColor

Choose techniques that complement the section type and overall tone. Be BOLD with gradients - subtle gradients are invisible!

## Animation Rules (MUST FOLLOW)

This page uses a consistent animation strategy. Apply these animations to EVERY section you generate:

### Section Entry Animation
- Add \`animationType="${outline.animationStrategy?.sectionEntry || 'fadeIn'}"\` to the \`<section>\` element
- Use \`animationDuration="800"\` for sections
- For sections with multiple columns/cards in a row, add \`staggerDelay="150"\` to the parent \`<row>\` so children animate sequentially
- Child columns inside a staggered row should ALSO have \`animationType="${outline.animationStrategy?.sectionEntry || 'fadeIn'}"\` with \`animationDuration="600"\`

### Card Hover Effects
${
  outline.animationStrategy?.cardHover && outline.animationStrategy.cardHover !== 'none'
    ? `- Add \`hoverEffect="${outline.animationStrategy.cardHover}"\` to card-like columns (feature cards, benefit cards, testimonial cards, pricing cards)
- NOT every column is a card — only add hover to columns that function as self-contained cards with their own background, border, or visual boundary
- Use default intensity values (no need to specify hoverIntensity unless you want custom values)`
    : '- No card hover effects for this page design'
}

### Image Hover Effects
${
  outline.animationStrategy?.imageHover && outline.animationStrategy.imageHover !== 'none'
    ? `- Add \`hoverEffect="${outline.animationStrategy.imageHover}"\` to SELECTED inline \`<image>\` elements — product images, gallery images, team photos
- Do NOT add hover to background images, decorative images, or icons
- Do NOT add hover to images that are purely informational (charts, diagrams)`
    : '- No image hover effects for this page design'
}

### Continuous Image Animations
${
  outline.animationStrategy?.imageContinuous && outline.animationStrategy.imageContinuous !== 'none'
    ? `- Add \`continuousAnimation="${outline.animationStrategy.imageContinuous}"\` ONLY to hero background images or large full-width images where subtle motion enhances the visual
- Do NOT add continuous animation to product images, thumbnails, avatars, or any small images
- Limit to 1-2 images per page maximum — overuse feels distracting
- Use default speed and intensity values`
    : '- No continuous image animations for this page design'
}

### Animation Don'ts
- Do NOT add entry animations to elements inside a staggered container — the stagger handles their timing
- Do NOT mix different animation types within the same section (e.g., slideUp on one column and fadeIn on another) — consistency is key
- Do NOT add animations to form fields or very small elements

## Business Context

- Business: ${outline.businessType} (${outline.industry})
- Tone: ${outline.tone}
- Audience: ${outline.targetAudience}
- Value proposition: ${outline.valueProposition}
- Page purpose: ${outline.pagePurpose}`;

  const user = buildUserPrompt(outline, sectionEntry, sectionIndex, totalSections);

  return { system, user };
};

/**
 * Build placehold.co documentation for placeholder images
 */
const getPlaceholderImageDocs = (): string => {
  return `## Placeholder Images (placehold.co)

When no suitable image is assigned, use placehold.co to generate contextual placeholders.

**URL Format:** https://placehold.co/{width}x{height}/{bgColor}/{textColor}?text={text}

**Common Sizes:**
- Hero/banner: 1200x600, 1400x700, 800x500
- Product: 600x600, 800x800 (square)
- Portrait/team: 400x500, 300x400
- Thumbnail: 300x300, 400x300
- Icon/avatar: 80x80, 100x100

**Text Parameter:** Use + for spaces. Keep text short (1-3 words). Only ASCII characters. No more than 3 words

**Examples:**
- Hero: https://placehold.co/1200x600/1a1a2e/ffffff?text=Hero+Image
- Product: https://placehold.co/600x600/f3f4f6/374151?text=Product
- Team member: https://placehold.co/400x500/e5e7eb/1f2937?text=Team+Photo
- Logo: https://placehold.co/200x60/transparent/374151?text=Logo

**Placeholder usage rules:**
- Placeholders can ONLY be used as inline <image> elements with an src attribute.
- NEVER use a placehold.co URL as a bgImage attribute on any container, section, row, or column. Placeholder images contain rendered text (like "Hero Image" or "Product") which looks broken and unprofessional when used as a CSS background image behind real content.
- If a section layout calls for a background image (hero-full-image, hero-image-left, hero-image-right, cta-image-bg) and no real image is available, use a solid dark background color or a gradient instead of a placeholder. For example: bg="gradient(linear, 135, #1a1a2e:0, #2d2d44:100)" or bg="rgba(30,30,50,1)".`;
};

/**
 * Build the image context section for the prompt
 */
const buildImageContext = (entry: SectionOutlineEntry): string => {
  const images = entry.resolvedImages || [];

  if (images.length === 0) {
    return `## Images

No real images have been assigned to this section, you have two options:
1. Use placehold.co for inline <image> elements only (e.g., product shots, team photos, thumbnails).
2. If this section's layout calls for a background image (hero-full-image, hero-image-left, hero-image-right, cta-image-bg), use a solid dark color or gradient as the background instead. For example: bg="gradient(linear, 135, #1a1a2e:0, #2d2d44:100)" or bg="#1a1a2e". Do NOT use a placehold.co URL as a bgImage — it will render text like "Hero Image" behind your content and look broken.

${getPlaceholderImageDocs()}`;
  }

  const imageList = images
    .map((img) => {
      const fitNote = img.fit === 'contain' ? ' (transparent - use fit="contain")' : '';
      const bgNote = img.suggestedBackground ? ` with bg="${img.suggestedBackground}"` : '';
      const brightnessNote = img.brightness ? `\n    brightness: ${img.brightness}` : '';
      const luminanceNote =
        img.luminance !== undefined ? ` (luminance: ${img.luminance.toFixed(2)})` : '';
      return `- ${img.role}: ${img.url}
    alt: "${img.alt}"
    dimensions: ${img.width}x${img.height}
    fit: ${img.fit}${fitNote}${bgNote}${brightnessNote}${luminanceNote}`;
    })
    .join('\n\n');

  return `## Assigned Images for This Section

CRITICAL: Use ONLY the exact URLs below. Do NOT invent, generate, or guess image URLs.
Copy the URLs verbatim. Any URL not in this list will result in a 404 error.

${imageList}

**Fallback:** If you need additional inline images beyond what's assigned, use placehold.co (format: https://placehold.co/{width}x{height}/{bgColor}/{textColor}?text={text}). Only use placehold.co as inline <image> elements — never as a bgImage on containers.`;
};

/**
 * Build the testimonial context section for the prompt
 */
const buildTestimonialContext = (entry: SectionOutlineEntry): string => {
  const testimonials = entry.resolvedTestimonials || [];

  if (testimonials.length === 0) {
    return '';
  }

  const testimonialList = testimonials
    .map((t, i) => {
      const ratingStars = '★'.repeat(t.rating) + '☆'.repeat(5 - t.rating);
      return `${i + 1}. "${t.text}"
   - Author: ${t.author}
   - Rating: ${ratingStars} (${t.rating}/5)
   - Date: ${t.date || 'Unknown'}`;
    })
    .join('\n\n');

  return `## Testimonials to Use

Include these real customer testimonials in this section:

${testimonialList}

Format testimonials with:
- Quote text in body.large or body.medium variant
- Author name and context
- Optional rating display`;
};

/**
 * Build section-type-specific rules
 */
const buildSectionTypeRules = (type: string, isFirst: boolean, isLast: boolean): string => {
  const rules: string[] = [];

  if (isFirst) {
    rules.push('## HERO SECTION RULES (This is the first section)');
    rules.push('- Use variant="heading.h1" for the main headline (ONLY h1 on entire page)');
    rules.push('- Strong headline: 3-8 words stating the core benefit');
    rules.push('- Subheadline with variant="body.large" that expands on the benefit');
    rules.push('- Include a primary CTA button with action-specific text');
    rules.push('- Hero sections should have generous padding and visual impact');
  }

  if (isLast) {
    rules.push('## FINAL SECTION RULES (This is the last section)');
    rules.push('- Create urgency or reinforce the value proposition');
    rules.push('- Include a prominent primary CTA button');
    rules.push('- Keep copy short and action-oriented');
    rules.push('- This is the last chance to convert the visitor');
  }

  switch (type) {
    case 'hero':
      if (!isFirst) {
        rules.push('## HERO SECTION RULES');
        rules.push('- Use variant="heading.h2" (h1 was used in the first section)');
      }
      rules.push('- Keep the message focused and scannable');
      rules.push('- Large, high-quality imagery');
      break;

    case 'features':
    case 'benefits':
      rules.push(`## ${type.toUpperCase()} SECTION RULES`);
      rules.push('- Use variant="heading.h2" for the section title');
      rules.push('- Use variant="heading.h3" for individual feature/benefit titles');
      rules.push(
        '- Each feature should have a specific, concrete description - not generic filler',
      );
      rules.push('- Use images that contextually match each feature');
      rules.push('- Cards should have subtle backgrounds for depth');
      break;

    case 'social-proof':
      rules.push('## SOCIAL PROOF SECTION RULES');
      rules.push('- Use variant="heading.h2" for the section title');
      rules.push('- Format testimonials with quote text, author name, and context');
      rules.push('- Use italic styling or larger body text for quotes');
      rules.push('- Include ratings if available');
      break;

    case 'cta':
      rules.push('## CTA SECTION RULES');
      rules.push('- Use variant="heading.h2" for the call-to-action headline');
      rules.push('- Keep copy short and action-oriented');
      rules.push('- Button text must be specific: "Start Free Trial", "Get Your Quote"');
      rules.push('- Consider using a dark or accent background for contrast');
      rules.push(
        '- Do NOT include a <form> in this section unless this is the only section on the page designated for lead capture. There should be exactly one form on the entire page — if a separate contact or newsletter section already has the form, this CTA section should use a button only. If this section does include a form, keep it simple: just full name (<shortText>) and email (<email>) fields plus a submit button.',
      );
      rules.push(
        '- COUNTDOWN TIMER: Only include a <countdown> element if the section purpose EXPLICITLY mentions a specific time-limited offer, sale, event deadline, or expiring promotion. Read the purpose field carefully — if it talks about general conversion, sign-ups, or lead capture without mentioning a deadline or limited-time aspect, do NOT add a countdown. When you do include one, use mode="evergreen" with evergreenDuration="604800" (1 week) for general urgency, or mode="standard" with a specific dateTime for events.',
      );
      break;

    case 'pricing':
      rules.push('## PRICING SECTION RULES');
      rules.push('- Use variant="heading.h2" for the section title');
      rules.push('- Use variant="heading.h3" for plan names');
      rules.push('- Include specific features per plan, not vague descriptions');
      rules.push('- Highlight the recommended plan visually');
      break;

    case 'contact':
      rules.push('## CONTACT SECTION RULES');
      rules.push('- Use variant="heading.h2" for the section title');
      rules.push(
        '- Include a <form> element with just two fields: a full name field (<shortText>) and an email field (<email>), plus a submit button. Keep the form simple — no phone, message, or other fields. The form is the core element of this section — it must be present.',
      );
      rules.push('- Add context text explaining what happens after submission');
      rules.push(
        '- Use action-specific submit button text like "Get My Free Quote" or "Request a Callback"',
      );
      break;

    case 'faq':
      rules.push('## FAQ SECTION RULES');
      rules.push('- Use variant="heading.h2" for the section title');
      rules.push('- Use variant="heading.h3" for each question');
      rules.push('- Answers should be specific to this business, not generic');
      break;

    case 'stats':
      rules.push('## STATS SECTION RULES');
      rules.push('- Use variant="heading.h2" for the section title');
      rules.push('- Use large, bold numbers for statistics');
      rules.push('- Each stat needs a clear label explaining what it measures');
      break;

    case 'gallery':
      rules.push('## GALLERY SECTION RULES');
      rules.push('- Use variant="heading.h2" for the section title if needed');
      rules.push('- Use assigned images with appropriate alt text');
      rules.push('- Consider grid layout for multiple images');
      break;

    case 'process':
      rules.push('## PROCESS SECTION RULES');
      rules.push('- Use variant="heading.h2" for the section title');
      rules.push('- Number each step clearly');
      rules.push('- Keep step descriptions brief and action-oriented');
      break;

    case 'logo-bar':
      rules.push('## LOGO BAR SECTION RULES');
      rules.push('- Use a simple heading like "Trusted by" or "Featured in"');
      rules.push('- Display logos in a horizontal row with consistent sizing');
      rules.push('- Padding around the logo grid must be uniform on all sides');
      rules.push('- Use grayscale or muted colors for logos to maintain visual harmony');
      rules.push('- Keep vertical padding tight (40-60px)');
      break;

    case 'newsletter':
      rules.push('## NEWSLETTER SECTION RULES');
      rules.push('- Use variant="heading.h2" for the headline');
      rules.push('- Include a compelling reason to subscribe');
      rules.push(
        '- Include a <form> element with just two fields: a full name field (<shortText>) and an email field (<email>), plus a submit button. Keep the form simple. The form is the core element of this section.',
      );
      rules.push('- Add trust element like "No spam, unsubscribe anytime"');
      break;

    case 'quote':
      rules.push('## QUOTE SECTION RULES');
      rules.push('- Feature a single prominent testimonial');
      rules.push('- Use larger text (body.large or heading.h3) for the quote');
      rules.push('- Include author name, title, and optionally company');
      rules.push('- Consider adding author photo if available');
      rules.push('- Use quotation marks or italic styling');
      break;

    case 'about':
      rules.push('## ABOUT SECTION RULES');
      rules.push('- Use variant="heading.h2" for the section title');
      rules.push('- Tell the company or brand story concisely');
      rules.push('- Include relevant imagery (founder photo, team, workspace)');
      rules.push('- Focus on mission, values, or unique backstory');
      break;

    case 'services':
      rules.push('## SERVICES SECTION RULES');
      rules.push('- Use variant="heading.h2" for the section title');
      rules.push('- Use variant="heading.h3" for individual service names');
      rules.push('- Each service needs a specific description of what it includes');
      rules.push('- Consider including pricing or "starting at" information');
      break;

    case 'portfolio':
      rules.push('## PORTFOLIO SECTION RULES');
      rules.push('- Use variant="heading.h2" for the section title');
      rules.push('- Showcase work with high-quality images');
      rules.push('- Include project names and brief descriptions');
      rules.push('- Consider grid or cards layout for multiple items');
      break;

    case 'awards':
      rules.push('## AWARDS SECTION RULES');
      rules.push('- Use simple heading like "Awards" or "Recognition"');
      rules.push('- Display badges, certifications, or press logos');
      rules.push('- Use consistent sizing for award imagery');
      rules.push('- Keep layout clean with tight spacing');
      break;

    case 'team':
      rules.push('## TEAM SECTION RULES');
      rules.push('- Use variant="heading.h2" for the section title');
      rules.push('- Include team member photos, names, and roles');
      rules.push('- Keep bios brief (1-2 sentences) if included');
      rules.push('- Use consistent image sizes and styling');
      break;

    case 'comparison':
      rules.push('## COMPARISON SECTION RULES');
      rules.push('- Use variant="heading.h2" for the section title');
      rules.push('- Create clear visual distinction between options');
      rules.push('- Use checkmarks/icons for feature presence');
      rules.push('- Highlight the recommended or featured option');
      break;
  }

  return rules.length > 0 ? rules.join('\n') : '';
};

/**
 * Build the user prompt with section details and continuity context
 */
const buildUserPrompt = (
  outline: PageProfileOutline,
  entry: SectionOutlineEntry,
  sectionIndex: number,
  totalSections: number,
): string => {
  const isFirst = sectionIndex === 0;
  const isLast = sectionIndex === totalSections - 1;

  // Derive previous sections context directly from the outline
  const previousSections = outline.sections.slice(0, sectionIndex);
  const continuityContext =
    previousSections.length > 0
      ? `

## Previous Sections on This Page

${previousSections.map((s, i) => `${i + 1}. ${s.type}: "${s.contentDirection.headline}"`).join('\n')}

Build on the narrative. Do not repeat information already covered. Each section should add new value.`
      : '';

  // Section-specific rules
  const sectionRules = buildSectionTypeRules(entry.type, isFirst, isLast);

  // Visual style context
  const visualStyleContext = buildVisualStyleContext(outline, entry);

  // Image and testimonial context
  const imageContext = buildImageContext(entry);
  const testimonialContext = buildTestimonialContext(entry);

  const layoutDescription = LAYOUT_PATTERN_DESCRIPTIONS[entry.layoutPattern] || entry.layoutPattern;

  return `Generate section ${sectionIndex + 1} of ${totalSections}: **${entry.type}**

## Layout
Use a **${entry.layoutPattern}** layout: ${layoutDescription}

## Purpose
${entry.purpose}

## Content Direction

- **Headline**: "${entry.contentDirection.headline}"
- **Subheadline theme**: ${entry.contentDirection.subheadlineTheme}
- **Body theme**: ${entry.contentDirection.bodyTheme}
${entry.contentDirection.ctaText ? `- **CTA**: "${entry.contentDirection.ctaText}"` : ''}

${visualStyleContext}

${imageContext}

${testimonialContext}

${sectionRules}
${continuityContext}

Generate the complete section XML with layout containers and all content elements. Start with <section> and end with </section>.`;
};

/**
 * Build visual style context for this section
 */
const buildVisualStyleContext = (
  outline: PageProfileOutline,
  entry: SectionOutlineEntry,
): string => {
  let context = `## Visual Style for This Section

**Page background color: ${outline.pageConfig.backgroundColor}** (this is what shows through transparent sections)

Background type: ${entry.visualStyle.backgroundType}
Background hint: ${entry.visualStyle.backgroundHint}
Spacing intensity: ${entry.spacingIntensity}`;

  if (entry.visualStyle.backgroundType === 'transparent') {
    context += `

**TRANSPARENT BACKGROUND**: Do NOT add a bg="" attribute to the section. Leave the section background empty so the page background color (${outline.pageConfig.backgroundColor}) shows through.
- For headlines and body text: OMIT color attribute to use global text color (already designed to contrast with ${outline.pageConfig.backgroundColor})
- For supporting/muted text: use semi-transparent versions like rgba(0,0,0,0.6) or rgba(255,255,255,0.7) depending on page background lightness
- For cards/containers: subtle backgrounds like rgba(255,255,255,0.5) or rgba(0,0,0,0.05) work well
- For placeholder images: choose colors that complement ${outline.pageConfig.backgroundColor}`;
  }

  if (entry.visualStyle.backgroundType === 'dark') {
    context += `

**DARK BACKGROUND**: Use light text colors. Set color="#ffffff" or color="rgba(255,255,255,0.9)" for main text. Use color="rgba(255,255,255,0.7)" for supporting text.`;
  }

  if (entry.visualStyle.backgroundType === 'accent') {
    context += `

**ACCENT BACKGROUND**: Ensure text has sufficient contrast with the accent color. Use white text for dark accents, dark text for light accents.`;
  }

  return context;
};
